import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes
} from "react"
import { Icon, type IconName } from "./Icon"

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "soft"
  size?: "sm" | "md" | "lg" | "icon"
  loading?: boolean
  icon?: IconName
}) {
  return (
    <button
      {...props}
      className={`button button--${variant} button--${size} ${className}`}
      disabled={loading || props.disabled}
    >
      {loading ? (
        <span className="spinner" aria-hidden="true" />
      ) : icon ? (
        <Icon name={icon} size={size === "sm" ? 16 : 19} />
      ) : null}
      {children}
    </button>
  )
}

export function IconButton({
  label,
  icon,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: IconName }) {
  return (
    <button className="icon-button" aria-label={label} title={label} {...props}>
      <Icon name={icon} />
    </button>
  )
}

export function Badge({
  children,
  tone = "neutral"
}: {
  children: ReactNode
  tone?: "neutral" | "success" | "warning" | "danger" | "violet"
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function Field({
  label,
  hint,
  error,
  children
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  const id = useId()
  return (
    <label className="field" data-field-id={id}>
      <span className="field__label">{label}</span>
      {children}
      {error ? (
        <span className="field__error">{error}</span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </label>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ""}`} />
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`textarea ${props.className ?? ""}`} />
}

export function Dialog({
  open,
  onClose,
  title,
  eyebrow,
  children,
  size = "md"
}: {
  open: boolean
  onClose(): void
  title: string
  eyebrow?: string
  children: ReactNode
  size?: "sm" | "md" | "lg"
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog ref={ref} className={`dialog dialog--${size}`} onClose={onClose} onCancel={onClose}>
      <div className="dialog__header">
        <div>
          {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
          <h2>{title}</h2>
        </div>
        <IconButton label="Закрыть" icon="x" onClick={onClose} />
      </div>
      <div className="dialog__body">{children}</div>
    </dialog>
  )
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  children
}: {
  open: boolean
  onClose(): void
  title: string
  description?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const sheet = ref.current
    if (!sheet) return
    if (open && !sheet.open) sheet.showModal()
    if (!open && sheet.open) sheet.close()
  }, [open])
  return (
    <dialog ref={ref} className="dialog sheet" onClose={onClose} onCancel={onClose}>
      <div className="sheet__handle" aria-hidden="true" />
      <div className="dialog__header">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        <IconButton label="Закрыть" icon="x" onClick={onClose} />
      </div>
      <div className="dialog__body">{children}</div>
    </dialog>
  )
}

export function EmptyState({
  icon,
  title,
  text,
  action
}: {
  icon: string
  title: string
  text: string
  action?: ReactNode
}) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon" aria-hidden="true">
        {icon}
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  )
}

export function LoadingState({ rows = 3 }: { rows?: number }) {
  return (
    <div className="skeleton-stack" aria-label="Загрузка">
      {Array.from({ length: rows }, (_, index) => (
        <div className="skeleton" key={index} />
      ))}
    </div>
  )
}

export function ErrorState({
  title = "Не получилось загрузить",
  message,
  retry
}: {
  title?: string
  message?: string
  retry?: () => void
}) {
  return (
    <div className="error-state" role="alert">
      <div className="error-state__icon">!</div>
      <div>
        <strong>{title}</strong>
        <p>{message ?? "Проверьте соединение и попробуйте ещё раз."}</p>
      </div>
      {retry ? (
        <Button variant="secondary" size="sm" onClick={retry}>
          Повторить
        </Button>
      ) : null}
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  )
}

export function Section({
  title,
  description,
  action,
  children,
  className = ""
}: {
  title?: string
  description?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`section ${className}`}>
      {title || description || action ? (
        <div className="section__header">
          <div>{title ? <h2>{title}</h2> : null}{description ? <p>{description}</p> : null}</div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  )
}

export function FormSection({
  title,
  description,
  optional = false,
  children
}: {
  title: string
  description?: string
  optional?: boolean
  children: ReactNode
}) {
  return (
    <fieldset className="form-section">
      <legend>
        <span>{title}</span>
        {optional ? <small>Необязательно</small> : null}
      </legend>
      {description ? <p className="form-section__description">{description}</p> : null}
      <div className="form-section__body">{children}</div>
    </fieldset>
  )
}

export function StickyActionBar({ children }: { children: ReactNode }) {
  return <div className="sticky-action-bar">{children}</div>
}

export function Segmented<T extends string>({
  value,
  options,
  onChange
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange(value: T): void
}) {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? "is-active" : ""}
          onClick={() => onChange(option.value)}
          role="tab"
          aria-selected={value === option.value}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

interface ToastMessage {
  id: number
  text: string
  tone: "success" | "danger" | "neutral"
}

let toastId = 0
export function showToast(text: string, tone: ToastMessage["tone"] = "success") {
  window.dispatchEvent(new CustomEvent("family-coin:toast", { detail: { id: ++toastId, text, tone } }))
}

export function ToastViewport() {
  const [messages, setMessages] = useState<ToastMessage[]>([])
  useEffect(() => {
    const listener = (event: Event) => {
      const message = (event as CustomEvent<ToastMessage>).detail
      setMessages((current) => [...current.slice(-2), message])
      window.setTimeout(() => setMessages((current) => current.filter((item) => item.id !== message.id)), 4_500)
    }
    window.addEventListener("family-coin:toast", listener)
    return () => window.removeEventListener("family-coin:toast", listener)
  }, [])
  return (
    <div className="toast-viewport" aria-live="polite">
      {messages.map((message) => (
        <div key={message.id} className={`toast toast--${message.tone}`}>
          <Icon name={message.tone === "success" ? "check" : message.tone === "danger" ? "x" : "bell"} size={18} />
          {message.text}
        </div>
      ))}
    </div>
  )
}
